import { saveBlobAsFile } from '@/api/apiUtils'
import { DownloadIcon, SearchIconBtn } from '@/assets/icons'
import { Search } from '@/components/common'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import CustomButton from '@/components/Returns/CustomButton'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MT_TIN_COLUMNS } from '@/multi-territory/mt-configs/mt-tax-identification-number'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import {
    deleteTanMaster,
    downloadTanMaster,
    listTanMaster,
} from '@/multi-territory/mt-services/mt-tan-master-api'
import type { MTCountryOption } from '@/multi-territory/mt-types/mt-admin'
import { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import {
    buildMTSearchField,
    formatMTHeaderLabel,
    formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import { ROUTES } from '@/routes/routeConfig'
import { ROLE_CAPABILITIES } from '@/utils/role-capabilities'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { MTCommonTable } from '../mt-shared/MTCommonTable'

const TINSearch = Search as any

export function MTAdminTaxIdentificationNumber() {
  // Hooks
  const roleTypes = useSelector((state: any) => state.user.role)
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.entityTan?.selectedEntityUuid ?? ''
  )
  const reduxCountryOptions: MTCountryOption[] =
    useSelector((state: any) => state.navigation.countryOptions) ||
    COUNTRY_OPTIONS
  const navigate = useNavigate()

  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE
  )
  const [data, setData] = useState<any[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tinToDelete, setTinToDelete] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: MT_TIN_COLUMNS[0]?.id || '',
      value: '',
    },
  ])

  const { showSnackbar } = useSnackbar()

  // Derived Data
  const roleConfig =
    ROLE_CAPABILITIES[roleTypes as keyof typeof ROLE_CAPABILITIES] ||
    ROLE_CAPABILITIES.default

  // Fetch TAN list from API
  const fetchList = useCallback(
    async (searchList: any[] = []) => {
      if (!countryCode) return

      setLoading(true)
      try {
        const result = await listTanMaster<any>({
          pageFilter: {
            currentPage,
            pageSize: itemsPerPage,
            sortField: 'created_date',
            sortOrder: 'DESC',
            includeCount: true,
          },
          searchCOList: searchList,
        })

        if (result.success && result.data) {
          const responseData = result.data
          const rawContent = responseData.content ?? responseData.data ?? []
          // Map response fields to row data for table columns
          const mappedData = rawContent.map((item: any) => ({
            ...item,
            countryName: item.countryName ?? item.countryCode ?? '-',
            entityName: item.entityName ?? '-',
            uniqueIdentificationNumber: item.uniqueIdentificationNumber ?? '-',
            taxIdentificationNumber: item.taxIdentificationNumber ?? '-',
            createdBy: item.createdBy ?? '-',
            createdDate: item.createdDate ?? '-',
            updatedBy: item.lastModifiedBy ?? item.updatedBy ?? '-',
            updatedDate: item.lastModifiedDate ?? item.updatedDate ?? '-',
          }))
          setData(mappedData)
          setTotalItems(
            responseData.totalElements ?? responseData.totalCount ?? 0
          )
        }
      } catch (error) {
        console.error('Error fetching TAN list:', error)
      } finally {
        setLoading(false)
      }
    },
    [countryCode, currentPage, itemsPerPage]
  )

  useEffect(() => {
    fetchList()
  }, [fetchList])

  // Handlers
  const handlePageChange = (page: number): void => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (size: number): void => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleEdit = (row: Record<string, unknown>): void => {
    navigate(ROUTES.MT_ADMIN_TIN_EDIT, {
      state: {
        editMode: true,
        uuid: row.tanMasterUuid || row.uuid,
        countryCode: row.countryCode ?? countryCode,
        countryName: row.countryName,
        entityMasterUuid: row.entityMasterUuid ?? selectedEntityUuid,
        attributes: row,
      },
    })
  }

  const handleDelete = (row: Record<string, unknown>): void => {
    setTinToDelete(row)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = (): void => {
    setDeleteDialogOpen(false)
    setTinToDelete(null)
  }

  const confirmDelete = async (): Promise<void> => {
    const uuid = (tinToDelete?.tanMasterUuid || tinToDelete?.uuid) as string
    if (!uuid) return

    try {
      const result = await deleteTanMaster(
        tinToDelete?.countryCode as string,
        uuid
      )

      if (result.success) {
        showSnackbar(result.data, 'success')
        setDeleteDialogOpen(false)
        setTinToDelete(null)
        fetchList()
      } else {
        showSnackbar(result.error ?? 'Failed to delete', 'error')
      }
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        'error'
      )
    }
  }

  const handleAdd = (): void => {
    navigate(ROUTES.MT_ADMIN_TIN_CREATE, {
      state: {
        countryCode,
        entityMasterUuid: selectedEntityUuid,
      },
    })
  }

  const handleDownload = async (): Promise<void> => {
    const payload = {
      pageFilter: {
        currentPage: 1,
        pageSize: totalItems || itemsPerPage,
      },
      searchCOList: [],
    }

    const response = await downloadTanMaster(payload)

    if (response.success) {
      saveBlobAsFile(response.data, 'tin-master.xlsx')
    } else {
      showSnackbar('Failed to download', 'error')
    }
  }

  const handleSearchOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setSearchAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }

  const handleSearchClose = (): void => {
    setSearchOpen(false)
    setSearchAnchorEl(null)
  }

  const handleApplySearch = (criteria: any[]): void => {
    const apiCriteria = formatMTSearchCriteria(criteria)
    const mappedCriteria = apiCriteria.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: item.id || idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    fetchList(apiCriteria)
  }

  const handleResetSearch = (): void => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: MT_TIN_COLUMNS[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    setSearchAnchorEl(null)
    fetchList([])
  }

  // Table columns for MTCommonTable (auto width, capped at 200px, wraps beyond).
  // Action column shown when the user can edit TIN; edit/delete are gated
  // separately (edit -> isEditTan, delete -> allScreens), matching prior behavior.
  const tableColumns = useMemo<MTMetadataTableColumn[]>(() => {
    const base = MT_TIN_COLUMNS.map(col => ({
      id: col.id,
      label: col.label,
      type: 'text' as const,
      sortable: false,
      editable: false,
    }))
    if (!roleConfig.isEditTan) return base
    return [
      ...base,
      {
        id: '_action',
        label: 'Actions',
        type: 'text' as const,
        sortable: false,
        editable: false,
        width: '120px',
      },
    ]
  }, [roleConfig.isEditTan])

  const findRow = (uuid: string): Record<string, unknown> | undefined =>
    data.find(
      r => (r.tanMasterUuid as string) === uuid || (r.uuid as string) === uuid
    )

  const handleRowEdit = (uuid: string): void => {
    const row = findRow(uuid)
    if (row) handleEdit(row)
  }

  const handleRowDelete = (uuid: string): void => {
    const row = findRow(uuid)
    if (row) handleDelete(row)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 500,
            lineHeight: '1.3em',
            color: 'var(--forbrand-secondary)',
          }}
        >
          {GLOBAL_CONSTANTS.MT_ADMIN_TIN_TITLE}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Download Excel">
            <IconButton size="medium" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search">
            <IconButton size="medium" onClick={handleSearchOpen}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
          {roleConfig.canEdit && (
            <>
              <Box
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--border-subtel)',
                }}
              />
              <CustomButton onClick={handleAdd}>
                {GLOBAL_CONSTANTS.MT_ADMIN_TIN_ADD}
              </CustomButton>
            </>
          )}
        </Box>
      </Box>

      {/* Table */}
      <MTCommonTable
        columns={tableColumns}
        data={data}
        loading={loading}
        onRowEdit={roleConfig.isEditTan ? handleRowEdit : undefined}
        onRowDelete={roleConfig.allScreens ? handleRowDelete : undefined}
        showFooter={true}
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onCancel={handleDeleteDialogClose}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this TIN?"
        message="On deletion of TIN, all the data pertaining to it would get delete. Do you want to continue ?"
        confirmText="Delete TIN"
        itemType="TIN"
        type="warning"
        showCancelButton={true}
      />

      {/* Search Popover */}
      <TINSearch
        open={searchOpen}
        anchorEl={searchAnchorEl}
        onClose={handleSearchClose}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        fields={MT_TIN_COLUMNS.map(col => {
          if (col.id === 'countryName') {
            return {
              ...col,
              label: formatMTHeaderLabel(col.label),
              type: 'dropdown',
              placeholder: 'Select country',
              options: reduxCountryOptions.map(c => ({
                label: c.country_name,
                value: c.country_name,
              })),
            }
          }
          return buildMTSearchField(col)
        })}
        placement="bottom-start"
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
      />
    </Box>
  )
}
